import { type DragEvent, useState, useSyncExternalStore } from "react";
import type { FileNode } from "../api.ts";

export const NODE_TYPE = "application/x-vault-node";

export type DraggedNode = Pick<FileNode, "id" | "name" | "kind" | "parentId">;
export type Landing = { parentId: string | null; name: string };
export type NodeDrop = { node: DraggedNode; into: Landing };

let dragged: DraggedNode | null = null;
let moving: NodeDrop | null = null;
const listeners = new Set<() => void>();

const notify = () => {
  for (const listener of listeners) listener();
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const setMoving = (drop: NodeDrop | null) => {
  moving = drop;
  notify();
};

export const useMoving = () => useSyncExternalStore(subscribe, () => moving);

export const carriesNode = (event: { dataTransfer: DataTransfer | null }) =>
  event.dataTransfer?.types.includes(NODE_TYPE) === true;

export const canLand = (node: DraggedNode, into: Landing) =>
  node.id !== into.parentId && node.parentId !== into.parentId;

export const requestMove = (drop: NodeDrop) =>
  window.dispatchEvent(new CustomEvent<NodeDrop>("drive:move", { detail: drop }));

export const useNodeDrag = (node: DraggedNode, enabled = true) => {
  const [dragging, setDragging] = useState(false);
  return {
    dragging,
    props: {
      draggable: enabled,
      onDragStart: (event: DragEvent<HTMLElement>) => {
        if (!enabled) return;
        event.dataTransfer.setData(NODE_TYPE, node.id);
        event.dataTransfer.effectAllowed = "move";
        const image = event.currentTarget.querySelector<HTMLElement>("[data-drag-image]");
        if (image) event.dataTransfer.setDragImage(image, 20, image.offsetHeight / 2);
        dragged = { id: node.id, name: node.name, kind: node.kind, parentId: node.parentId };
        setDragging(true);
      },
      onDragEnd: () => {
        dragged = null;
        setDragging(false);
      },
    },
  };
};

/** Handlers return true when they claimed the event, so a target can fall through to uploads. */
export const useNodeDrop = (into: Landing, enabled = true) => {
  const [over, setOver] = useState(false);
  const accepts = (event: DragEvent<HTMLElement>) =>
    enabled && dragged !== null && carriesNode(event) && canLand(dragged, into);
  return {
    over,
    dragOver: (event: DragEvent<HTMLElement>) => {
      if (!accepts(event)) return false;
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = "move";
      setOver(true);
      return true;
    },
    dragLeave: (event: DragEvent<HTMLElement>) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOver(false);
    },
    drop: (event: DragEvent<HTMLElement>) => {
      if (!accepts(event) || dragged === null) return false;
      event.preventDefault();
      event.stopPropagation();
      setOver(false);
      requestMove({ node: dragged, into });
      dragged = null;
      return true;
    },
  };
};
